import React, { useState, useEffect } from 'react';
import { Form, Input, Checkbox, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { departmentAPI } from '../../services/organizationAPI';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const DepartmentEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState<any[]>([]);
    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<any>({
        onSave: async (data) => {
            if (isNew) {
                const result = await departmentAPI.create(data);
                message.success('部门已创建');
                navigate('/organization');
            } else {
                await departmentAPI.update(parseInt(id), data);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        // Fetch modules for permissions
        setModules([
            { id: 'sku_management', name: 'SKU管理' },
            { id: 'operation_strategy', name: '运营策略' },
            { id: 'data_center', name: '数据中心' },
            { id: 'supply_chain', name: '供应链' },
            { id: 'finance', name: '财务' },
        ]);

        if (!isNew) {
            fetchDepartment();
        }
    }, [id]);

    const fetchDepartment = async () => {
        setLoading(true);
        try {
            const res = await departmentAPI.get(parseInt(id!));
            form.setFieldsValue(res.data);
        } catch (error) {
            message.error('加载部门信息失败');
            navigate('/organization');
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            await departmentAPI.create(values);
            message.success('部门已创建');
            navigate('/organization');
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? '新增部门' : '编辑部门'}
            breadcrumbs={[
                { title: '组织管理', path: '/organization' },
                { title: '部门管理' },
                { title: isNew ? '新增' : '编辑' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                <Form.Item name="name" label="部门名称" rules={[{ required: true, message: '请输入部门名称' }]}>
                    <Input placeholder="如：运营部、技术部" />
                </Form.Item>

                <Form.Item name="description" label="部门描述">
                    <Input.TextArea rows={3} placeholder="部门职能描述" />
                </Form.Item>

                <Form.Item name="accessible_modules" label="可访问模块">
                    <Checkbox.Group>
                        {modules.map(m => (
                            <Checkbox key={m.id} value={m.id}>{m.name}</Checkbox>
                        ))}
                    </Checkbox.Group>
                </Form.Item>
            </Form>
        </FormPageLayout>
    );
};

export default DepartmentEdit;
